import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X, Monitor, Smartphone } from 'lucide-react';
import { Button } from './Button';

interface OnboardingSlide {
  id: string;
  title: string;
  description: string;
  desktopImage: string;
  mobileImage: string;
}

interface StudioOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
}

const slides: OnboardingSlide[] = [
  {
    id: 'welcome',
    title: 'Добро пожаловать в Dissai Studio!',
    description: 'Ваш персональный ассистент для создания контента с искусственным интеллектом. Давайте познакомимся с основными возможностями платформы.',
    desktopImage: '/images/onboarding/desktop/welcome.png',
    mobileImage: '/images/onboarding/mobile/welcome.png'
  },
  {
    id: 'audience',
    title: 'Целевая аудитория',
    description: 'Анализируйте и сегментируйте вашу аудиторию для создания более точного и эффективного контента.',
    desktopImage: '/images/onboarding/desktop/audience.png',
    mobileImage: '/images/onboarding/mobile/audience.png'
  },
  {
    id: 'reels-analysis',
    title: 'Анализ Reels',
    description: 'Получайте подробный анализ успешных Reels: от структуры до ключевых элементов, которые сделали их вирусными.',
    desktopImage: '/images/onboarding/desktop/reels.png',
    mobileImage: '/images/onboarding/mobile/reels.png'
  },
  {
    id: 'content-plan',
    title: 'Контент-план',
    description: 'Генерируйте детальные планы контента с учетом ваших целей и целевой аудитории. ИИ поможет создать эффективную стратегию.',
    desktopImage: '/images/onboarding/desktop/content-plan.png',
    mobileImage: '/images/onboarding/mobile/content-plan.png'
  },
  {
    id: 'knowledge-base',
    title: 'Мои документы',
    description: 'Создавайте и организуйте статьи, конспекты, цели и другие материалы. Ваша персональная база знаний всегда под рукой.',
    desktopImage: '/images/onboarding/desktop/knowledge.png',
    mobileImage: '/images/onboarding/mobile/knowledge.png'
  }
];

export function StudioOnboarding({ isOpen, onClose }: StudioOnboardingProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = () => {
    // Сохраняем информацию о том, что пользователь прошел onboarding
    localStorage.setItem('studio_onboarding_completed', 'true');
    localStorage.setItem('studio_onboarding_date', new Date().toISOString());
    onClose();
  };

  const handleSkip = () => {
    handleFinish();
  };

  if (!isOpen) return null;

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden relative"
        >
          {/* Кнопка закрытия */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>

          <div className="flex flex-col lg:flex-row h-full">
            {/* Левая часть - Текст */}
            <div className="flex-1 p-6 lg:p-8 flex flex-col justify-center">
              {/* Индикатор прогресса */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <span>{currentSlide + 1} из {slides.length}</span>
                  <div className="flex items-center">
                    {isMobile ? <Smartphone className="w-4 h-4 mr-1" /> : <Monitor className="w-4 h-4 mr-1" />}
                    <span>{isMobile ? 'Мобильная версия' : 'Десктоп версия'}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                  />
                </div>
              </div>

              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {currentSlideData.title}
              </h2>
              
              <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-8">
                {currentSlideData.description}
              </p>

              {/* Кнопки навигации */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Пропустить
                </button>

                <div className="flex items-center space-x-4">
                  {/* Точки индикации */}
                  <div className="flex space-x-2">
                    {slides.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentSlide 
                            ? 'bg-blue-500' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>

                  <Button
                    onClick={handleNext}
                    className="flex items-center space-x-2 px-6 py-3"
                  >
                    <span>{isLastSlide ? 'Начать работу' : 'Далее'}</span>
                    {!isLastSlide && <ChevronRight className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Правая часть - Изображение */}
            <div className="flex-1 bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6 lg:p-8">
              <div className="relative w-full h-full max-w-md mx-auto">
                <img
                  src={isMobile ? currentSlideData.mobileImage : currentSlideData.desktopImage}
                  alt={currentSlideData.title}
                  className="w-full h-full object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    // Заглушка для отсутствующих изображений
                    const target = e.target as HTMLImageElement;
                    target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f3f4f6'/%3E%3Ctext x='200' y='150' text-anchor='middle' fill='%236b7280' font-family='Arial' font-size='16'%3EСкриншот: ${currentSlideData.title}%3C/text%3E%3C/svg%3E`;
                  }}
                />
                
                {/* Overlay с информацией о версии */}
                <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs">
                  {isMobile ? 'Mobile' : 'Desktop'}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
}

// Константа для включения/выключения onboarding
const ONBOARDING_ENABLED = false;

// Хук для проверки, нужно ли показывать onboarding
export function useStudioOnboarding() {
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);

  useEffect(() => {
    if (!ONBOARDING_ENABLED) {
      return;
    }
    
    const completed = localStorage.getItem('studio_onboarding_completed');
    if (!completed) {
      // Небольшая задержка, чтобы страница успела загрузиться
      const timer = setTimeout(() => {
        setShouldShowOnboarding(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const hideOnboarding = () => {
    setShouldShowOnboarding(false);
  };

  return {
    shouldShowOnboarding,
    hideOnboarding
  };
}